import React, { useRef, useEffect, useState, useCallback } from 'react';
import { Box, Button, Typography } from '@mui/material';
import BYDBanner from './Asset/banner-DenzaD9.webp';
import BYDBannerMobile from './Asset/denzad9-banner-mob.webp';
import DenzaD9Black from './Asset/Denza_D9_EV-black.webp';
import DenzaD9White from './Asset/Denza_D9_EV-white.webp';
import DenzaD9Grey from './Asset/Denza_D9_EV-grey.webp';
import DenzaD9Blue from './Asset/Denza_D9_EV-blue.webp';
import DenzaD9Interior from './Asset/interior.webp';
import DenzaD9Sunroof from './Asset/m07-pc-skylight.webp';
import DenzaD9beige from './Asset/interior-beige.webp';
import DenzaD9brown from './Asset/interior-brown.webp';
import { Helmet } from 'react-helmet-async';

function JelajahiDenza() {
  const sections = useRef([]);
  const [scrollIndex, setScrollIndex] = useState(0);
  const [visibleSections, setVisibleSections] = useState({});
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [selectedColor, setSelectedColor] = useState('black');
  const [selectedInterior, setSelectedInterior] = useState('beige');

  const colorOptions = [
    { label: 'Black', color: 'black', image: DenzaD9Black },
    { label: 'White', color: 'white', image: DenzaD9White },
    { label: 'Grey', color: 'grey', image: DenzaD9Grey },
    { label: 'Blue', color: 'darkblue', image: DenzaD9Blue },
  ];

  const interiorOptions = [
    { label: 'Beige', color: 'beige', image: DenzaD9beige },
    { label: 'Brown', color: 'brown', image: DenzaD9brown },
  ];

  const currentImage = colorOptions.find(
    (option) => option.color === selectedColor
  )?.image;
  const currentInteriorImage = interiorOptions.find(
    (option) => option.color === selectedInterior
  )?.image;

  const images = [
    {
      src: isMobile ? BYDBannerMobile : BYDBanner,
      title: 'A Luxurious New Energy Vehicle DENZA D9',
      specs: [
        { value: '520 km', label: 'WLTC all-electric range' },
        { value: '150 km', label: '10-minute-charge range' },
        { value: '6.9s', label: '0-100km/h acceleration' },
      ],
    },
    {
      src: currentImage,
      customContent: (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            gap: 4,
            marginTop: 2,
          }}
        >
          {colorOptions.map((option) => (
            <Box
              key={option.color}
              onClick={() => setSelectedColor(option.color)}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                cursor: 'pointer',
              }}
            >
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  backgroundColor: option.color,
                  borderRadius: '50%',
                  border:
                    selectedColor === option.color
                      ? '2px solid #000'
                      : '2px solid transparent',
                  marginBottom: 1,
                }}
              />
              <Typography variant='body2'>{option.label}</Typography>
            </Box>
          ))}
        </Box>
      ),
    },
    {
      src: currentInteriorImage,
      title: `Interior Color: ${
        interiorOptions.find((option) => option.color === selectedInterior)
          ?.label
      }`,
      customContent: (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            gap: 4,
            marginTop: 2,
          }}
        >
          {interiorOptions.map((option) => (
            <Box
              key={option.color}
              onClick={() => setSelectedInterior(option.color)}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                cursor: 'pointer',
              }}
            >
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  backgroundColor:
                    option.color === 'beige' ? '#F5F5DC' : '#8B4513',
                  borderRadius: '50%',
                  border:
                    selectedInterior === option.color
                      ? '2px solid #000'
                      : '2px solid transparent',
                  marginBottom: 1,
                }}
              />
              <Typography variant='body2'>{option.label}</Typography>
            </Box>
          ))}
        </Box>
      ),
    },
    {
      src: DenzaD9Sunroof,
      title: 'Sunroof: Pandangan Luas',
      description: `Sunroof panoramik ekstra besar kami menghadirkan cahaya alami, membuat kabin terasa terang dan lapang, dilengkapi dengan kaca dua lapis yang mampu memblokir 99% sinar UV.`,
    },
    {
      src: DenzaD9Interior,
      description: `Gambar di atas hanya untuk referensi. Semua perlengkapan mengikuti spesifikasi aktual model setir kanan untuk Indonesia.`,
    },
  ];

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // const handleScroll = (e) => {
  //   e.preventDefault();
  //   const delta = e.deltaY;

  //   if (delta > 0 && scrollIndex < images.length - 1) {
  //     setScrollIndex((prev) => prev + 1);
  //   } else if (delta < 0 && scrollIndex > 0) {
  //     setScrollIndex((prev) => prev - 1);
  //   }
  // };

  const SCROLL_THRESHOLD = 50;

  const handleScroll = useCallback(
    (e) => {
      e.preventDefault();
      let accumulatedDelta = 0;

      accumulatedDelta += e.deltaY;

      if (
        accumulatedDelta > SCROLL_THRESHOLD &&
        scrollIndex < images.length - 1
      ) {
        setScrollIndex((prev) => prev + 1);
        accumulatedDelta = 0;
      } else if (accumulatedDelta < -SCROLL_THRESHOLD && scrollIndex > 0) {
        setScrollIndex((prev) => prev - 1);
        accumulatedDelta = 0;
      }
    },
    [scrollIndex, images.length] // Dependencies of handleScroll
  );

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const updatedVisibleSections = { ...visibleSections };
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            updatedVisibleSections[entry.target.dataset.index] = true;
          }
        });
        setVisibleSections(updatedVisibleSections);
      },
      { threshold: 0.5 }
    );

    const currentSections = sections.current; // Copy to a variable
    currentSections.forEach((section) => {
      if (section) {
        observer.observe(section);
      }
    });

    return () => {
      currentSections.forEach((section) => {
        if (section) {
          observer.unobserve(section);
        }
      });
    };
  }, [visibleSections]);

  useEffect(() => {
    window.addEventListener('wheel', handleScroll, { passive: false });

    return () => {
      window.removeEventListener('wheel', handleScroll);
    };
  }, [handleScroll]);

  useEffect(() => {
    sections.current[scrollIndex]?.scrollIntoView({ behavior: 'smooth' });
  }, [scrollIndex]);

  return (
    <>
      <Helmet>
        <title>BYD Denza D9 - Dealer Resmi di BSD Serpong</title>
        <meta
          name='description'
          content='Temukan BYD Denza D9 di Dealer Resmi BYD BSD Serpong. Dapatkan promo terbaik untuk mobil listrik di lokasi Anda!'
        />
        <meta
          name='keywords'
          content='BYD Denza D9 BSD Serpong, Dealer Mobil Listrik BSD Serpong, Harga BYD Denza D9 Serpong, BYD BSD Serpong'
        />
        <meta name='author' content='BYD BSD Serpong Junius' />
        <meta name='geo.region' content='ID-BT' />
        <meta
          name='geo.placename'
          content='BSD Serpong, Tangerang Selatan, Indonesia'
        />
        <meta name='geo.position' content='-6.3046;106.6496' />
        <meta name='ICBM' content='-6.3046, 106.6496' />
        <script type='application/ld+json'>
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Product',
            name: 'BYD Denza D9',
            image:
              'https://bydbsdserpong.com/static/media/banner-DenzaD9.2542005342edf9f85567.webp',
            description:
              'SUV listrik terbaru dengan performa tinggi, dirancang oleh Wolfgang Egger.',
            brand: {
              '@type': 'Brand',
              name: 'BYD',
            },
            offers: {
              '@type': 'Offer',
              price: 950000000,
              priceCurrency: 'IDR',
              priceValidUntil: '2025-12-31',
              availability: 'https://schema.org/InStock',
              url: 'https://bydbsdserpong.com',
            },
            aggregateRating: {
              '@type': 'AggregateRating',
              ratingValue: 4.8,
              reviewCount: 72,
            },
            review: [
              {
                '@type': 'Review',
                author: {
                  '@type': 'Person',
                  name: 'Junius',
                },
                datePublished: '2025-01-01',
                reviewBody:
                  'The BYD Denza D9 is an excellent electric car with great features!',
                reviewRating: {
                  '@type': 'Rating',
                  ratingValue: 5,
                  bestRating: 5,
                },
              },
              {
                '@type': 'Review',
                author: {
                  '@type': 'Person',
                  name: 'Sarah',
                },
                datePublished: '2025-01-05',
                reviewBody:
                  'Comfortable, eco-friendly, and stylish. Highly recommend this vehicle.',
                reviewRating: {
                  '@type': 'Rating',
                  ratingValue: 4.5,
                  bestRating: 5,
                },
              },
            ],
          })}
        </script>
      </Helmet>

      <Typography
        variant='h1'
        component='h1'
        sx={{
          fontSize: { xs: '2rem', sm: '3rem' },
          textAlign: 'center',
          marginBottom: 4,
        }}
      >
        Jelajahi BYD Denza D9
      </Typography>

      <Box>
        {images.map((img, index) => (
          <Box
            key={index}
            ref={(el) => (sections.current[index] = el)}
            data-index={index}
            sx={{
              position: 'relative',
              height: '100vh',
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
            }}
          >
            <Box
              component='img'
              src={img.src}
              alt={img.title || `Image ${index + 1}`}
              sx={
                isMobile
                  ? ['DenzaD9Sunroof', 'DenzaD9Interior'].some((name) =>
                      img.src.includes(name)
                    )
                    ? {
                        width: '100%', // Fullscreen width for mobile
                        height: '100vh', // Fullscreen height for these specific images
                        objectFit: 'cover', // Proper fit without white space
                        zIndex: -1,
                      }
                    : [
                        'DenzaD9beige',
                        'DenzaD9brown',
                        'DenzaD9Black',
                        'DenzaD9White',
                        'DenzaD9Grey',
                        'DenzaD9Blue',
                      ].some((name) => img.src.includes(name))
                    ? {
                        width: '100%', // Full width for car color images
                        height: 'auto', // Maintain aspect ratio
                        objectFit: 'contain', // No cropping
                        display: 'block', // Prevent layout issues
                        margin: '0 auto', // Center image on the page
                        zIndex: -1,
                      }
                    : {
                        width: '100%', // Full width for other images
                        height: 'auto', // Maintain aspect ratio
                        objectFit: 'contain', // Default behavior for other images
                        zIndex: -1,
                      }
                  : {
                      width: 'auto', // Auto width for desktop
                      height: 'auto', // Maintain aspect ratio for desktop
                      objectFit: 'contain', // Proper desktop rendering
                      zIndex: -1,
                    }
              }
            />

            {img.title && (
              <Box
                sx={{
                  position: 'absolute',
                  top: { xs: '10%', sm: '15%' },
                  color: '#fff',
                  textAlign: 'center',
                  padding: 2,
                }}
              >
                <Typography variant='h4' fontWeight='bold'>
                  {img.title}
                </Typography>
              </Box>
            )}

            {img.specs && (
              <Box
                sx={{
                  position: 'absolute',
                  bottom: { xs: '10%', sm: '5%' },
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  justifyContent: 'center',
                  gap: 2,
                  color: '#fff',
                }}
              >
                {img.specs.map((spec, i) => (
                  <Box key={i} sx={{ textAlign: 'center' }}>
                    <Typography variant='h6' fontWeight='bold'>
                      {spec.value}
                    </Typography>
                    <Typography variant='body2'>{spec.label}</Typography>
                  </Box>
                ))}
              </Box>
            )}

            {img.description && (
              <Box
                sx={{
                  position: 'absolute',
                  bottom: { xs: '15%', sm: '10%' },
                  color: '#fff',
                  textAlign: 'center',
                  padding: 2,
                }}
              >
                <Typography>{img.description}</Typography>
              </Box>
            )}

            {img.customContent && (
              <Box
                sx={{
                  position: 'absolute',
                  bottom: '5%',
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                {img.customContent}
              </Box>
            )}
          </Box>
        ))}
        <Box
          sx={{
            padding: { xs: 3, sm: 6 },
            backgroundColor: '#f5f5f5',
            textAlign: 'center',
          }}
        >
          <Typography
            variant='h2'
            sx={{
              fontSize: { xs: '1.5rem', sm: '2rem' },
              fontWeight: 600,
              mb: 2,
            }}
          >
            Tertarik dengan Denza D9?
          </Typography>
          <Typography variant='body1' sx={{ mb: 3 }}>
            Dapatkan info lengkap dan promo terbaru{' '}
            <strong>Harga BYD Atto1</strong> hanya di{' '}
            <strong>BYD BSD Serpong</strong>. Kami juga menyediakan informasi
            lengkap tentang <strong>Harga Denza D9</strong>,{' '}
            <strong>Harga Sealion 7</strong>, dan <strong>Harga Atto1</strong>{' '}
            langsung dari dealer resmi.
          </Typography>
          <Button
            href='https://api.whatsapp.com/send?phone=6281286864825&text=Halo%20saya%20tertarik%20dengan%20BYD%20DenzaD9%20dan%20ingin%20tahu%20harganya'
            target='_blank'
            variant='contained'
            color='primary'
          >
            Tanya Harga Sekarang
          </Button>
        </Box>
      </Box>
    </>
  );
}

export default JelajahiDenza;
